/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { Ref, useRef, useState } from 'preact/hooks';
import { getRandomId } from '../../utils/PRIVATE_visLayoutUtils/layoutUtils';
import { getInfo, cancelEvent } from '../../utils/UNSAFE_visUtils';
import { ChartGroupInfo, Info } from '../../utils/UNSAFE_visTypes/chart';
import { useVisHover } from '#hooks/PRIVATE_useVisHover';
import { mergeProps } from '#utils/UNSAFE_mergeProps';

type Props = {
  rootRef: Ref<HTMLElement>;
  getNextChartItem: (
    info: Info,
    arrow: 'ArrowLeft' | 'ArrowRight' | 'ArrowDown' | 'ArrowUp'
  ) => Info;
  getNextNavGroup: (info: ChartGroupInfo, arrow: 'ArrowLeft' | 'ArrowRight') => ChartGroupInfo;
  getDefaultNavGroup: (info: ChartGroupInfo) => ChartGroupInfo | undefined;
  xIndexExtent: { startIndex: number; endIndex: number };
  touchResponse: 'touchStart' | 'touchHold';
  onItemHover?: (detail: { seriesIndex?: number; groupIndex?: number }) => void;
  onItemFocus?: (detail: { seriesIndex?: number; groupIndex?: number }) => void;
  drilling?: 'on' | 'groupsOnly' | 'off';
};

/**
 * Returns whether the two groups are same.
 * @param g1 GroupInfo
 * @param g2 GroupInfo
 * @returns
 */
function isGroupEqual(g1?: Partial<ChartGroupInfo>, g2?: Partial<ChartGroupInfo>) {
  return (
    g1?.groupIndex === g2?.groupIndex &&
    g1?.isCurrent === g2?.isCurrent &&
    g1?.isFocusVisible === g2?.isFocusVisible
  );
}

/**
 * Returns whether the two item info are same.
 * @param i1 ItemInfo
 * @param i2 ItemInfo
 * @returns
 */
function isItemEqual(i1?: Info, i2?: Info) {
  return i1?.seriesIndex === i2?.seriesIndex && isGroupEqual(i1, i2);
}

/**
 * Returns whether the two item info are same.
 * @param i1 ItemInfo
 * @param i2 ItemInfo
 * @returns
 */
function isItemInfoEqual(i1?: Info, i2?: Info) {
  return i1?.seriesIndex === i2?.seriesIndex && i2?.groupIndex === i1?.groupIndex;
}

/**
 * Hook to handle chart navigation.
 */
export function useChartNav({
  rootRef,
  getNextChartItem,
  getDefaultNavGroup,
  getNextNavGroup,
  xIndexExtent,
  touchResponse,
  onItemHover,
  onItemFocus,
  drilling
}: Props) {
  const [focusedItemInfo, setfocusedItemInfo] = useState<Info>({
    groupIndex: xIndexExtent.startIndex,
    seriesIndex: 0
  });

  const isGroupDrillEnabled = drilling === 'on' || drilling === 'groupsOnly';
  const [hoveredItemInfo, setHoveredItemInfo] = useState<Info>();

  const [focusedGroupInfo, setFocusedGroupInfo] = useState<ChartGroupInfo>({ groupIndex: 0 });
  const activeId = useRef<string>();
  const previousHoveredItemRef = useRef<Info>();
  const previousFocusedItemRef = useRef<Info>();

  const isItemFocused = focusedItemInfo.isCurrent;
  const isGroupFocused = focusedGroupInfo.isCurrent;

  const updateGroupInfo = (groupInfo: ChartGroupInfo) => {
    if (!isGroupEqual(groupInfo, focusedGroupInfo)) {
      setFocusedGroupInfo(groupInfo);
    }
  };

  const update = (type: 'hover' | 'focus', info?: Info) => {
    const prevInfoRef = type === 'hover' ? previousHoveredItemRef : previousFocusedItemRef;
    const callback = type === 'hover' ? onItemHover : onItemFocus;
    if (!isItemEqual(info, prevInfoRef.current)) {
      const input = { groupIndex: info?.groupIndex, seriesIndex: info?.seriesIndex };
      callback?.(input);
      prevInfoRef.current = info;
      activeId.current = getRandomId();
    }
  };

  const updateFocusedItemInfo = (itemInfo: Info) => {
    if (!isItemEqual(itemInfo, focusedItemInfo)) {
      setfocusedItemInfo(itemInfo);
      // preserve hovered effect if same item is focused
      if (hoveredItemInfo && !isItemInfoEqual(hoveredItemInfo, itemInfo)) {
        setHoveredItemInfo({ ...hoveredItemInfo, isCurrent: false });
      }
      if (itemInfo.isCurrent) {
        update('focus', itemInfo);
      }
    }
  };

  const updateHoveredItemInfo = (itemInfo?: Info) => {
    if (!hoveredItemInfo || !isItemEqual(itemInfo, hoveredItemInfo)) {
      setHoveredItemInfo(itemInfo);
      setfocusedItemInfo({ ...focusedItemInfo, isCurrent: false });
      update('hover', itemInfo);
    }
  };

  const handleTab = (event: KeyboardEvent) => {
    const defaultGroup = getDefaultNavGroup(focusedGroupInfo);
    const validItemInfoIndex = Math.max(
      xIndexExtent.startIndex,
      Math.min(xIndexExtent.endIndex, focusedItemInfo.groupIndex!)
    );
    if (event.shiftKey) {
      if (isItemFocused) {
        updateFocusedItemInfo({
          ...focusedItemInfo,
          groupIndex: validItemInfoIndex,
          isCurrent: false,
          isFocusVisible: false
        });
        update('focus', undefined);
      } else if (isGroupFocused) {
        updateFocusedItemInfo({
          ...focusedItemInfo,
          groupIndex: validItemInfoIndex,
          isCurrent: true,
          isFocusVisible: true
        });
        if (isGroupDrillEnabled) {
          updateGroupInfo({
            ...focusedGroupInfo,
            ...defaultGroup,
            isCurrent: false,
            isFocusVisible: false
          });
          cancelEvent(event);
        }
      } else if (isGroupDrillEnabled) {
        updateGroupInfo({
          ...focusedGroupInfo,
          ...defaultGroup,
          isCurrent: true,
          isFocusVisible: true
        });
      }
    } else {
      if (isItemFocused) {
        updateFocusedItemInfo({
          ...focusedItemInfo,
          groupIndex: validItemInfoIndex,
          isCurrent: false,
          isFocusVisible: false
        });
        if (isGroupDrillEnabled) {
          updateGroupInfo({
            ...focusedGroupInfo,
            ...defaultGroup,
            isCurrent: true,
            isFocusVisible: true
          });
          cancelEvent(event);
        }
        update('focus', undefined);
      } else if (isGroupFocused && isGroupDrillEnabled) {
        updateGroupInfo({
          ...focusedGroupInfo,
          ...defaultGroup,
          isCurrent: false,
          isFocusVisible: false
        });
      } else {
        updateFocusedItemInfo({
          ...focusedItemInfo,
          groupIndex: validItemInfoIndex,
          isCurrent: true,
          isFocusVisible: true
        });
      }
    }
    activeId.current = getRandomId();
  };

  const onKeyUp = (event: KeyboardEvent) => {
    const key = event.key;
    switch (key) {
      case 'Tab': {
        if (!isItemFocused && !isGroupFocused) {
          handleTab(event);
        }
        break;
      }
    }
  };

  const onKeyDown = (event: KeyboardEvent) => {
    const key = event.key;
    switch (key) {
      case 'Tab': {
        handleTab(event);
        break;
      }
      case 'ArrowRight': {
        if (isGroupFocused) {
          const info = getNextNavGroup(focusedGroupInfo, key);
          updateGroupInfo({
            ...info,
            isCurrent: true,
            isFocusVisible: true
          });
        } else {
          const info = getNextChartItem(focusedItemInfo, key);
          updateFocusedItemInfo({
            ...info,
            isCurrent: true,
            isFocusVisible: true
          });
        }
        setHoveredItemInfo(undefined);
        cancelEvent(event);
        break;
      }
      case 'ArrowLeft': {
        if (isGroupFocused) {
          const info = getNextNavGroup(focusedGroupInfo, key);
          updateGroupInfo({
            ...info,
            isCurrent: true,
            isFocusVisible: true
          });
        } else {
          const info = getNextChartItem(focusedItemInfo, key);
          updateFocusedItemInfo({
            ...info,
            isCurrent: true,
            isFocusVisible: true
          });
        }
        setHoveredItemInfo(undefined);
        cancelEvent(event);
        break;
      }
      case 'ArrowUp': {
        if (isItemFocused) {
          const info = getNextChartItem(focusedItemInfo, key);
          updateFocusedItemInfo({
            ...info,
            isCurrent: true,
            isFocusVisible: true
          });
        }
        setHoveredItemInfo(undefined);
        cancelEvent(event);
        break;
      }
      case 'ArrowDown': {
        if (isItemFocused) {
          const info = getNextChartItem(focusedItemInfo, key);
          updateFocusedItemInfo({
            ...info,
            isCurrent: true,
            isFocusVisible: true
          });
        }
        setHoveredItemInfo(undefined);
        cancelEvent(event);
        break;
      }
    }
  };

  const onPointerUp = (event: PointerEvent) => {
    const info = getInfo(rootRef, event.target as HTMLElement);

    if (!info) {
      return;
    }
    const { seriesIndex, groupIndex } = info;

    if (seriesIndex != undefined && groupIndex != undefined) {
      updateFocusedItemInfo({
        seriesIndex,
        groupIndex,
        isCurrent: false,
        isFocusVisible: false
      });
      updateGroupInfo({
        ...focusedGroupInfo,
        isCurrent: false,
        isFocusVisible: false
      });
    } else if (groupIndex !== undefined && seriesIndex === undefined) {
      updateGroupInfo({
        groupIndex,
        isCurrent: true,
        isFocusVisible: false
      });
      updateFocusedItemInfo({
        ...focusedItemInfo,
        isCurrent: false,
        isFocusVisible: false
      });
    }
  };

  const onHover = (event: PointerEvent) => {
    const info = getInfo(rootRef, event.target as HTMLElement);
    if (!info) {
      updateHoveredItemInfo(undefined);
      return;
    }
    const { seriesIndex, groupIndex } = info;

    if (seriesIndex != undefined) {
      updateHoveredItemInfo({
        seriesIndex,
        groupIndex,
        isCurrent: true
      });
      updateFocusedItemInfo({
        ...focusedItemInfo,
        isCurrent: false
      });
    }
  };

  const onHoverLeave = () => {
    updateHoveredItemInfo(undefined);
  };

  const hoverHandlers = useVisHover(onHover, undefined, onHoverLeave, touchResponse);

  const onFocusUpdate = () => {
    const validItemInfoIndex = Math.max(
      xIndexExtent.startIndex,
      Math.min(xIndexExtent.endIndex, focusedItemInfo.groupIndex!)
    );
    updateFocusedItemInfo({
      ...focusedItemInfo,
      groupIndex: validItemInfoIndex,
      isCurrent: true,
      isFocusVisible: true
    });
  };

  const onBlur = () => {
    updateFocusedItemInfo({
      ...focusedItemInfo,
      isCurrent: false,
      isFocusVisible: false
    });
  };

  return {
    focusedItemInfo,
    focusedGroupInfo,
    hoveredItemInfo,
    activeId: activeId.current,
    navProps: mergeProps(hoverHandlers, {
      onKeyDown,
      onKeyUp,
      onPointerUp,
      onBlur
    }),
    onFocusUpdate
  };
}
